import { IconBox, IconBriefcase, IconQuestionMark, IconSearch, IconUser, IconUsersGroup } from "@tabler/icons-react";
import { useRouter } from "next/router";
import { forwardRef, useState } from "react";

import { ArchivableLabelCell } from "~/components/_design-system/datatable/cells/ArchivableLabelCell";
import { Autocomplete, Flex, Loader, Text, useDebouncedValue } from "~/ui";
import { api } from "~/utils/api";

const icons: Record<string, JSX.Element> = {
  CUSTOMER: <IconUser />,
  SUPPLIER: <IconUsersGroup />,
  PRODUCT: <IconBox />,
  "PRODUCT CODE": <IconBox />,
  "PURCHASE ORDER": <IconBox />,
  "WORK ORDER": <IconBox />,
  "SALES ORDER": <IconBriefcase />,
};

interface UniversalSearchItemProps {
  entityType: string;
  entityId: string;
  label: string;
  url: string;
  hint?: string;
  archivedAt?: Date | null;
}

const UniversalSearchItem = forwardRef<HTMLDivElement, UniversalSearchItemProps>(
  ({ entityType, entityId: _, url: __, label, hint, archivedAt, ...others }: UniversalSearchItemProps, ref) => {
    const icon = icons[entityType] || <IconQuestionMark />;
    return (
      <div ref={ref} {...others}>
        <Flex align="flex-start" direction="column" gap={4}>
          <Flex align="center" gap="xs" style={{ minWidth: 0, width: "100%" }}>
            {icon}
            <Flex align="center" style={{ minWidth: 0, flex: 1 }}>
              <ArchivableLabelCell archivedAt={archivedAt ?? null} label={label} truncate={false} />
            </Flex>
          </Flex>
          {hint ? (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          ) : null}
        </Flex>
      </div>
    );
  },
);
UniversalSearchItem.displayName = "UniversalSearchItem";

export const UniversalSearch = () => {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 200);
  const searchResults = api.search.search.useQuery({ query: debouncedQuery }, { enabled: debouncedQuery.length > 0 });
  return (
    <Autocomplete
      withinPortal
      placeholder="Search ShelfCycle"
      w="100%"
      value={query}
      onChange={setQuery}
      icon={query !== debouncedQuery || searchResults.isFetching ? <Loader size={16} /> : <IconSearch size={16} />}
      data={searchResults.data?.map(r => ({ ...r, value: r.label })) || []}
      filter={() => true}
      itemComponent={UniversalSearchItem}
      onItemSubmit={r => {
        router.push(r.url);
        setQuery("");
      }}
      limit={10} // CRITICAL: Must match server UNIVERSAL_SEARCH_RESULT_LIMIT. Default Mantine limit is 5.
    />
  );
};
